import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Tooltip } from '@fluentui/react-components';
import { Transaction, TransactionInput } from '../types';
import { TransactionRow } from './TransactionRow';
import { EditableRow } from './EditableRow';
import { InputRow } from './InputRow';
import { MobileTransactionCard } from './MobileTransactionCard';
import { MobileAddTransaction } from './MobileAddTransaction';
import { useIsMobile } from '../hooks/useIsMobile';
import '../styles/register.css';

interface RegisterTableProps {
  transactions: Transaction[];
  startingBalance: number;
  onAdd: (input: TransactionInput) => void;
  onUpdate: (id: number, input: TransactionInput) => void;
  onToggleCleared: (id: number) => void;
  onDelete: (id: number) => void;
}

export function useRegisterTable({
  transactions, startingBalance, onAdd, onUpdate, onToggleCleared, onDelete, accountId,
}: RegisterTableProps & { accountId: number | null }) {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [mobileAddOpen, setMobileAddOpen] = useState(false);
  const isMobile = useIsMobile();
  const today = new Date().toISOString().split('T')[0];

  const lastPastIdx = useMemo(() => {
    let idx = -1;
    transactions.forEach((t, i) => {
      if (t.date <= today) idx = i;
    });
    return idx;
  }, [transactions, today]);

  const balances = useMemo(() => {
    let bal = startingBalance;
    return transactions.map(t => {
      bal += (t.deposit || 0) - (t.payment || 0);
      return bal;
    });
  }, [transactions, startingBalance]);

  const { clearedBalance, unclearedBalance } = useMemo(() => {
    let cleared = startingBalance;
    let uncleared = 0;
    transactions.forEach(t => {
      const amt = (t.deposit || 0) - (t.payment || 0);
      if (t.cleared) cleared += amt;
      else uncleared += amt;
    });
    return { clearedBalance: cleared, unclearedBalance: uncleared };
  }, [transactions, startingBalance]);

  const currentBalance = balances.length > 0 ? balances[balances.length - 1] : startingBalance;

  const handleSave = (id: number, data: TransactionInput) => {
    onUpdate(id, data);
    setEditingId(null);
  };

  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (transactions.length > 0) {
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 100);
    }
  }, [accountId, transactions.length]);

  const table = isMobile ? (
    <div className="register-container">
      <div className="mobile-card-list">
        {transactions.map((txn, idx) => {
          const isFuture = txn.date > today;
          return (
            <MobileTransactionCard
              key={txn.id}
              txn={txn}
              balance={balances[idx]}
              isFuture={isFuture}
              onToggleCleared={onToggleCleared}
              onEdit={setEditingId}
              onDelete={onDelete}
            />
          );
        })}
      </div>
      <button className="mobile-fab" onClick={() => setMobileAddOpen(true)} title="Add transaction">+</button>
      <MobileAddTransaction
        open={mobileAddOpen}
        onClose={() => setMobileAddOpen(false)}
        onAdd={onAdd}
        accountId={accountId}
      />
      <div ref={bottomRef} />
    </div>
  ) : (
    <div className="register-container">
      <table className="register-table">
        <thead>
          <tr>
            <Tooltip content="Transaction date" relationship="label"><th className="col-date">Date</th></Tooltip>
            <Tooltip content="Transaction type: Deposit, Draft, Check, Cash, or Memo" relationship="label"><th className="col-action">Action</th></Tooltip>
            <Tooltip content="Who the money is going to or coming from" relationship="label"><th className="col-payee">Payee</th></Tooltip>
            <Tooltip content="Amount going out of your account" relationship="label"><th className="col-money">Payment</th></Tooltip>
            <Tooltip content="Amount coming into your account" relationship="label"><th className="col-money">Deposit</th></Tooltip>
            <Tooltip content="Running balance after this transaction" relationship="label"><th className="col-money">Balance</th></Tooltip>
            <Tooltip content="Optional notes for your reference" relationship="label"><th className="col-notes">Notes</th></Tooltip>
            <Tooltip content="Check when transaction has cleared your bank" relationship="label"><th className="col-clr">Clr</th></Tooltip>
            <th className="col-actions"></th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((txn, idx) => {
            const isFuture = txn.date > today;
            const isDivider = idx === lastPastIdx && lastPastIdx < transactions.length - 1;

            if (editingId === txn.id) {
              return (
                <EditableRow
                  key={txn.id}
                  txn={txn}
                  balance={balances[idx]}
                  onSave={handleSave}
                  onCancel={() => setEditingId(null)}
                  onToggleCleared={onToggleCleared}
                />
              );
            }

            return (
              <TransactionRow
                key={txn.id}
                txn={txn}
                balance={balances[idx]}
                isFuture={isFuture}
                isDivider={isDivider}
                onToggleCleared={onToggleCleared}
                onEdit={setEditingId}
                onDelete={onDelete}
              />
            );
          })}
        </tbody>
        <tfoot>
          <InputRow onAdd={onAdd} accountId={accountId} />
        </tfoot>
      </table>
      <div ref={bottomRef} />
    </div>
  );

  return { currentBalance, clearedBalance, unclearedBalance, table };
}
